import React from "react";
import { cn } from "./cn";
import { Spinner } from "./Spinner";

export type ButtonVariant = "primary" | "secondary" | "outline" | "text" | "ghost" | "danger" | "danger-ghost" | "whatsapp";
export type ButtonSize = "sm" | "md" | "lg";

const VARIANTS: Record<ButtonVariant, string> = {
  primary: "bg-primary text-on-primary shadow-cta hover:bg-primary-hover disabled:bg-line-strong disabled:opacity-100 disabled:shadow-none",
  secondary: "border border-line-strong bg-surface text-fg hover:bg-surface-hover",
  outline: "border-strong border-primary bg-surface text-primary hover:bg-primary-tint",
  text: "text-primary hover:bg-primary-tint",
  ghost: "text-fg-muted hover:bg-surface-hover hover:text-fg",
  danger: "bg-danger text-white hover:bg-danger-hover",
  "danger-ghost": "text-danger hover:bg-danger-soft",
  whatsapp: "bg-whatsapp text-white hover:bg-whatsapp-hover",
};

const SIZES: Record<ButtonSize, string> = {
  sm: "h-11 px-button-sm text-label",
  md: "h-12 px-button text-heading",
  lg: "h-12 px-button text-heading",
};

export interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant;
  size?: ButtonSize;
  loading?: boolean;
  icon?: React.ReactNode;
  fullWidth?: boolean;
}

export const Button: React.FC<ButtonProps> = ({
  variant = "secondary",
  size = "md",
  loading = false,
  icon,
  fullWidth = false,
  disabled,
  type = "button",
  className,
  children,
  ...rest
}) => (
  <button
    {...rest}
    type={type}
    disabled={disabled || loading}
    aria-busy={loading || undefined}
    className={cn(
      "relative inline-flex items-center justify-center rounded-button font-semibold transition-colors",
      "focus-visible:outline-none focus-visible:shadow-focus disabled:cursor-not-allowed disabled:opacity-50",
      VARIANTS[variant],
      SIZES[size],
      // Largura cheia pode encolher (dois lado a lado dividem a linha); os demais não.
      fullWidth ? "w-full min-w-0 text-center" : "shrink-0",
      className
    )}
  >
    {/* O conteúdo fica invisível (não removido) durante o loading: o botão não muda de largura. */}
    {loading && (
      <span className="absolute inset-0 flex items-center justify-center">
        <Spinner size={{ sm: 14, md: 16, lg: 20 }[size]} />
      </span>
    )}
    <span className={cn("inline-flex items-center gap-2", loading && "invisible")}>
      {icon}
      {children}
    </span>
  </button>
);
